/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
export enum ViewModelState {
  Ready = 'ready',
  Playing = 'playing'
};
  
export enum PlayDirection {
  Next = 'next',
  Previous = 'previous'
};

/**
 * Get the initial time negative or positive time based on 
 * direction. Deligated to TimeSlider.tsx
 * 
 *```typescript
 *
 *  import {getBestTimeDiff} from '@ion/dls/src/maps/helpers';
 *
 *  const bestDiff = getBestTimeDiff(PlayDirection.Next);
 *
 *```
 */
export const getBestTimeDiff = (direction: PlayDirection) => {
  const time   = Math.abs((new Date(0, 0, 0)).valueOf());
  return direction === PlayDirection.Next ? -time : time;
};

/**
  * 
  * This function takes three arguments as input. First param is current timeslider value minus
  * time of event in the dataset secound param is the positive or negative initial time based 
  * on the direction and last param is direction is either next or previous. This will
  * returns boolean value based on the PlayDirection.
  * 
  * ```typescript
  * import {isValidAdvancement} from '@ion/dls/src/maps/helpers';
  *
  * const condition = isValidAdvancement(5, 3, PlayDirection.Next);
  *
  * ```
  * 
  * @param   current The current time slider value minus the time of the event in the dataset
  * @param   initial A time value based on the playback direction
  * @param   direction A positive or negative playback value
  * @returns If `direction` is set to `PlayDirection.Next`, returns `true` if the current
  *          value is less than zero and greater than the initial value, otherwise, returns
  *          true if current is greater than zero but less than the initial value, 
  */
export const isValidAdvancement = (current: number, initial: number, direction: PlayDirection): boolean => {
  if (direction === PlayDirection.Next) {
    return current < 0 && current > initial;
  }
  return current > 0 && current < initial;
};

/**
 * This function return boolean by comparing current state of timeslider with passed ViewModelState state. 
 * Deligated to TimeSlider.tsx
 * 
 *```typescript
 *
 *  import {isViewMode} from '@ion/dls/src/maps/helpers';
 *
 *  const isReadyMode = isViewMode(ViewModelState.Ready, 'ready');
 *  or
 *  const isPlayMode = isViewMode(ViewModelState.Playing, 'playing');
 *
 *```
 */
export const isViewMode = (mode: ViewModelState, state: ViewModelState) => mode === state;

/**
 * This function checks for timeslider state- 'ready' & returns boolean value.
 * Deligated to TimeSlider.tsx
 * 
 *```typescript
 *
 *  import {isReadyMode} from '@ion/dls/src/maps/helpers';
 *
 *  const viewState   = timeSlider?.viewModel?.state;
 *  const isReadyMode = isReadyMode(viewState);
 *
 *```
 */
export const isReadyMode = (state: ViewModelState) => isViewMode(ViewModelState.Ready, state);

/**
 * This function checks for timeslider state- 'playing' & returns boolean value. 
 * Deligated to TimeSlider.tsx
 * 
 *```typescript
 *
 *  import {isPlayMode} from '@ion/dls/src/maps/helpers';
 *
 *  const viewState = timeSlider?.viewModel?.state;
 *  const isPlayMode = isPlayMode(viewState);
 *
 *```
 */
export const isPlayMode = (state: ViewModelState) => isViewMode(ViewModelState.Playing, state);
